import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Kit, PortalConfig } from '@repo/types';
import usePartner from 'services/partner/usePartner';
import { getAllActivatedNonVoidedKits } from 'store/kits';

export type PortalConfigSource =
  | 'cobranded_partner'
  | 'kit_partner'
  | 'kit_practitioner'
  | 'aggregated'
  | 'default';

export type UsePortalConfigsResult = {
  config: PortalConfig;
  source: PortalConfigSource;
  isLoading: boolean;
};

const defaultConfig: PortalConfig = {
  disable_kit_activation: false,
  skip_surveys: false,
  hide_upsells: false,
  display_partner_logo: false,
  survey_type: 'full' as const,
};

// A key present but null means "no opinion", not "false" — the backend writes
// portal_config that way (`hide_upsells: value ?? undefined`), so a null must
// fall through to the layer below instead of clobbering it.
const withoutUnsetKeys = (config: PortalConfig): Partial<PortalConfig> =>
  Object.fromEntries(
    Object.entries(config).filter(([, value]) => value != null),
  );

const resolveKitPortalConfig = (
  kit: Kit | null | undefined,
): { config: PortalConfig; source: PortalConfigSource } => {
  const kitPartnerConfig = kit?.partner?.portal_config;
  const kitPractitionerConfig = kit?.practitioner?.portal_config;

  if (!kitPartnerConfig && !kitPractitionerConfig) {
    return { config: defaultConfig, source: 'default' };
  }

  // Partner and practitioner layer per key, not per object: `partners.portal_config`
  // has a `{}` column default, and an object-level check treated that empty object
  // as a full config, silently discarding every practitioner setting under it.
  return {
    config: {
      ...defaultConfig,
      ...(kitPractitionerConfig ? withoutUnsetKeys(kitPractitionerConfig) : {}),
      ...(kitPartnerConfig ? withoutUnsetKeys(kitPartnerConfig) : {}),
    },
    source: kitPartnerConfig ? 'kit_partner' : 'kit_practitioner',
  };
};

const aggregatePortalConfigs = (kits: Kit[]): PortalConfig => {
  if (!kits.length) return defaultConfig;
  const configs = kits.map(kit => resolveKitPortalConfig(kit).config);
  const surveyTypes = configs
    .map(c => c.survey_type)
    .filter((t): t is NonNullable<typeof t> => !!t);
  // If any kit requires 'skip', the aggregate is 'skip'; if any requires 'short', it's 'short'; otherwise 'full'
  const aggregatedSurveyType = surveyTypes.includes('skip')
    ? ('skip' as const)
    : surveyTypes.includes('short')
      ? ('short' as const)
      : ('full' as const);
  return {
    ...defaultConfig,
    hide_upsells: configs.some(c => c.hide_upsells),
    display_partner_logo: configs.some(c => c.display_partner_logo),
    disable_kit_activation: configs.some(c => c.disable_kit_activation),
    skip_surveys: configs.some(c => c.skip_surveys),
    activate_kit_on_delivery_to_lab: configs.some(
      c => c.activate_kit_on_delivery_to_lab,
    ),
    survey_type: aggregatedSurveyType,
  };
};

/**
 * Resolves portal config for a specific kit or aggregates across all kits.
 *
 * @param kit - Pass a Kit for per-kit resolution, null for "no kit yet" (returns default),
 *              or omit entirely for global aggregate mode (OR of all active kits).
 */
export const usePortalConfigs = (kit?: Kit | null): UsePortalConfigsResult => {
  const partnerQuery = usePartner();

  const allKitsSelector = useMemo(() => getAllActivatedNonVoidedKits(), []);
  const allActivatedKits = useSelector(allKitsSelector);

  const result = useMemo<UsePortalConfigsResult>(() => {
    // 1. Cobranded partner (subdomain) always takes precedence
    const partner = partnerQuery.data;
    if (partner?.portal_config) {
      return {
        config: {
          ...defaultConfig,
          ...partner.portal_config,
        },
        source: 'cobranded_partner',
        isLoading: Boolean(partnerQuery.isLoading || partnerQuery.isFetching),
      };
    }

    // 2. Per-kit mode: resolve from the provided kit
    if (kit !== undefined) {
      const { config, source } = resolveKitPortalConfig(kit);
      return { config, source, isLoading: false };
    }

    // 3. Global mode (no kit argument): aggregate across all active kits
    if (allActivatedKits.length > 0) {
      return {
        config: aggregatePortalConfigs(allActivatedKits),
        source: 'aggregated',
        isLoading: false,
      };
    }

    return {
      config: defaultConfig,
      source: 'default',
      isLoading: Boolean(partnerQuery.isLoading || partnerQuery.isFetching),
    };
  }, [
    kit,
    allActivatedKits,
    partnerQuery.data,
    partnerQuery.isFetching,
    partnerQuery.isLoading,
  ]);

  return result;
};
